import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { ButtonHTMLAttributes, forwardRef } from 'react';

import { generateDashedBorderStyle } from 'src/shared/lib/border-utils';
import { cn } from 'src/shared/lib/utils';

const buttonVariants = cva(
  'h-9 px-4 py-2 inline-flex whitespace-nowrap items-center select-none justify-center rounded-sm text-sm font-medium transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-white hover:bg-primary/80 h-[34px]',
        destructive: 'bg-destructive text-white hover:bg-destructive/80 h-[34px]',
        warning: 'bg-orange-600 text-white hover:bg-orange-500/80 h-[34px]',
        outline: 'border border-input bg-background hover:bg-accent hover:text-typography-black',
        secondary: 'bg-slate-100 text-neutral-950 hover:bg-slate-200 h-[34px]',
        ghost: 'hover:bg-neutral-100 hover:text-neutral-950 h-[34px]',
        link: 'text-primary underline-offset-4 hover:underline',
        dashed: 'text-typography-darkgrey hover:bg-border/20',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants>,
    WithTestId {
  asChild?: boolean;
  dashLength?: number;
  dashGap?: number;
  dashColor?: string;
  dashRadius?: number;
  strokeWidth?: number;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, asChild = false, testId, dashLength, dashGap, dashColor, dashRadius, strokeWidth, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';

    // Generate dashed border class if variant is dashed
    const dashedBorderClass =
      variant === 'dashed'
        ? generateDashedBorderStyle({
            dashLength,
            dashGap,
            color: dashColor || '#E5E6E6',
            borderRadius: dashRadius || 4,
            strokeWidth: strokeWidth || 3,
          })
        : '';

    return (
      <Comp
        className={cn(buttonVariants({ variant, className }), dashedBorderClass)}
        ref={ref}
        data-testid={testId}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
